import { faBolt, faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useMemo, useRef } from 'react';

import PERMISSIONS from '@/constants/auth.constants';
import { AUTO_SOC_TIERS } from '@/constants/products.constants';
import {
  organizationsAtom,
  selectedOrganizationAtom,
  selectedOrganizationIdAtom
} from '@/pages/Organizations';
import { isLayoutMobileAtom } from '@/store/layout';
import { isSidebarOpenAtom } from '@/store/sidebar';
import { userHasAllOrgAtom } from '@/store/user.store';

import { CheckPermissions } from '../CheckPermissions';
import useStyles from './OrganizationDropdown.styles';

const OrganizationDropdown = ({
  persistTheme
}: {
  persistTheme?: 'dark' | 'light';
}) => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [, setIsSidebarOpen] = useAtom(isSidebarOpenAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [, setSelectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [userHasAllOrg] = useAtom(userHasAllOrgAtom);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const { classes } = useStyles({ isLayoutMobile });

  const handleOrganizationChange = (newValue: {
    label: string;
    id: number;
  }) => {
    if (isLayoutMobile) setIsSidebarOpen(false);
    setSelectedOrganizationId(newValue.id);
    inputRef.current?.blur();
  };

  const orgList = useMemo(() => {
    const orgs = (organizations || []).map((org) => ({
      label: org.name,
      id: org.id,
      code: org.label,
      is_auto_soc: org.is_auto_soc,
      is_restricted: org.is_restricted
    }));
    orgs.unshift({
      label: 'All Organizations',
      id: 0,
      code: 'all',
      is_auto_soc: 0,
      is_restricted: false
    });
    return orgs;
  }, [organizations]);

  const selectedOrg = {
    label: selectedOrganization.name,
    id: selectedOrganization.id,
    code: selectedOrganization.label,
    is_auto_soc: selectedOrganization.is_auto_soc,
    is_restricted: selectedOrganization.is_restricted
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: {
      label: string;
      id: number;
      code: string;
      is_auto_soc: number;
      is_restricted: boolean;
    }
  ) => (
    <Box
      component="li"
      key={option.id}
      sx={{
        justifyContent: 'space-between !important',
        ...(isLayoutMobile && {
          backgroundColor: `#ffffff0D`,
          borderBottom: `1px solid ${theme.vars.palette.secondary.dark}`
        })
      }}
      {...props}
    >
      {option.label}
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
        {!!option.is_auto_soc && <FontAwesomeIcon icon={faBolt} />}
        {option.is_restricted && (
          <CheckPermissions permissions={[PERMISSIONS.INTERNAL]}>
            <FontAwesomeIcon icon={faLock} />
          </CheckPermissions>
        )}
        <Typography variant="body2" component="span" sx={{ opacity: 0.5 }}>
          {option.code}
        </Typography>
      </Stack>
    </Box>
  );

  // NOTE: renderInput is invoked by Autocomplete during its own render, so
  // hooks (e.g. useForkRef) must not be called inside it. Merge refs with a
  // plain callback instead.
  const mergeInputRefs =
    (autocompleteRef: React.Ref<HTMLInputElement> | undefined) =>
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof autocompleteRef === 'function') {
        autocompleteRef(node);
      } else if (autocompleteRef) {
        (
          autocompleteRef as React.MutableRefObject<HTMLInputElement | null>
        ).current = node;
      }
    };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      slotProps={{
        ...params.slotProps,
        input: {
          ...params.slotProps.input,
          startAdornment: (
            <InputAdornment position="start">
              <Chip
                className={classes.orgLabel}
                label={isLayoutMobile ? 'ORG' : 'Organization:'}
                onClick={() => inputRef.current?.focus()}
              />
            </InputAdornment>
          )
        },
        htmlInput: {
          ...params.slotProps.htmlInput,
          ref: mergeInputRefs(params.slotProps.htmlInput.ref)
        }
      }}
    />
  );

  return (
    userHasAllOrg && (
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 1 }}
        {...(persistTheme && { 'data-mui-color-scheme': persistTheme })}
      >
        <Autocomplete
          className={classes.orgAutoComplete}
          disableClearable
          disablePortal
          filterOptions={(options, params) =>
            options.filter((option) =>
              JSON.stringify(option)
                .toLowerCase()
                .includes(params.inputValue.toLowerCase())
            )
          }
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.id}
          id="mainOrganizationDropdown"
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(_, newValue) => handleOrganizationChange(newValue)}
          openOnFocus
          options={orgList}
          renderInput={renderInput}
          renderOption={renderOption}
          size="small"
          value={selectedOrg}
        />
        {!!selectedOrg.is_auto_soc && !isLayoutMobile && (
          <Chip
            icon={<FontAwesomeIcon icon={faBolt} />}
            label={`Agentic SOC${selectedOrg.is_auto_soc === AUTO_SOC_TIERS.DEFEND ? ` +` : ''}`}
            variant="outlined"
            color="info"
          />
        )}
        <CheckPermissions permissions={[PERMISSIONS.INTERNAL]}>
          {selectedOrg.is_restricted && !isLayoutMobile && (
            <Chip
              icon={<FontAwesomeIcon icon={faLock} />}
              label="Restricted"
              variant="outlined"
              color="warning"
            />
          )}
        </CheckPermissions>
      </Stack>
    )
  );
};

export default OrganizationDropdown;
